import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { attendanceService } from '../services/attendanceService';
import { usersService, User } from '../services/usersService';
import { format, startOfWeek, endOfWeek, isSameDay, parseISO, differenceInMinutes, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { filterUsersByRole } from '../utils/userFiltering';
import { QuickDateRange } from '../components/common/QuickDateRange';
import { DateRangePreset } from '../utils/dateRangeUtils';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Clock,
  Calendar as CalendarIcon,
  Users,
  Search,
  Filter,
  Download,
  AlertCircle,
  CheckCircle2,
  MoreHorizontal,
  Edit,
  Trash2,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  List,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

interface AttendanceEntry {
  id: string;
  userId: string;
  userName: string;
  date: string;
  actualIn?: string;
  actualOut?: string;
  totalHours: number;
  status: 'present' | 'incomplete' | 'unscheduled';
  originalInId?: string;
  originalOutId?: string;
}

interface EditFormState {
  date: string;
  clockIn: string;
  clockOut: string;
}

const ModernAttendanceManagement: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();

  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState<User[]>([]);
  const [attendanceEntries, setAttendanceEntries] = useState<AttendanceEntry[]>([]);
  const [startDate, setStartDate] = useState(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(() => {
    return (localStorage.getItem('attendance_mgmt_selectedPreset') as DateRangePreset) || 'thisWeek';
  });

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AttendanceEntry | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({ date: '', clockIn: '', clockOut: '' });

  // Persistence and View Settings
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>(() => {
    return (localStorage.getItem('attendance_mgmt_viewMode') as 'list' | 'calendar') || 'list';
  });
  const [calendarView, setCalendarView] = useState<'day' | 'week' | 'month'>(() => {
    return (localStorage.getItem('attendance_mgmt_calendarView') as 'day' | 'week' | 'month') || 'month';
  });
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());

  // Load persistent filters
  useEffect(() => {
    const savedStaffId = localStorage.getItem('attendance_mgmt_staffId');
    if (savedStaffId) setSelectedStaffId(savedStaffId);
    
    const savedStartDate = localStorage.getItem('attendance_mgmt_startDate');
    if (savedStartDate) setStartDate(savedStartDate);
    
    const savedEndDate = localStorage.getItem('attendance_mgmt_endDate');
    if (savedEndDate) setEndDate(savedEndDate);

    const savedPreset = localStorage.getItem('attendance_mgmt_selectedPreset');
    if (savedPreset) setSelectedPreset(savedPreset as DateRangePreset);
  }, []);

  // Save persistent settings
  useEffect(() => {
    localStorage.setItem('attendance_mgmt_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('attendance_mgmt_calendarView', calendarView);
  }, [calendarView]);

  useEffect(() => {
    localStorage.setItem('attendance_mgmt_staffId', selectedStaffId);
  }, [selectedStaffId]);

  useEffect(() => {
    localStorage.setItem('attendance_mgmt_startDate', startDate);
    localStorage.setItem('attendance_mgmt_endDate', endDate);
    localStorage.setItem('attendance_mgmt_selectedPreset', selectedPreset);
  }, [startDate, endDate, selectedPreset]);

  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  // Sync calendar date when filter changes
  useEffect(() => {
    if (startDate) {
      try {
        const newDate = parseISO(startDate);
        if (!isNaN(newDate.getTime())) {
          setCurrentCalendarDate(newDate);
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
  }, [startDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, rawAttendanceData] = await Promise.all([
        usersService.getUsers({ page: 1, size: 1000 }),
        attendanceService.getAttendanceRecords().catch(() => [])
      ]);

      const usersArray = Array.isArray(usersData) ? usersData : (usersData as any).data || [];
      const attendanceData = Array.isArray(rawAttendanceData) ? rawAttendanceData : (rawAttendanceData as any).data || [];
      
      // Filter users based on role hierarchy
      const filteredUsers = filterUsersByRole(usersArray, user?.roles || []);

      console.log('Attendance Debug:', {
        usersCount: usersArray.length,
        filteredUsersCount: filteredUsers.length,
        attendanceCount: attendanceData.length,
        firstFewUsers: filteredUsers.slice(0, 3).map((u: User) => ({ id: u.id, name: u.username })),
        firstFewAttendance: attendanceData.slice(0, 3).map((a: any) => ({ userId: a.userId, date: a.date })),
        range: { startDate, endDate }
      });

      setStaff(filteredUsers);

      // Process and pair attendance records
      const processedEntries = processAttendanceData(filteredUsers, attendanceData);
      console.log('Processed Entries:', processedEntries.length);
      setAttendanceEntries(processedEntries);
    } catch (error) {
      console.error('Error loading attendance data:', error);
      notify('Failed to load attendance data', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const processAttendanceData = (users: User[], attendance: any[]): AttendanceEntry[] => {
    const entries: AttendanceEntry[] = [];
    const days = getDateRangeArray(new Date(startDate), new Date(endDate));

    users.forEach(staffMember => {
      days.forEach(dateStr => {
        // Find all records for this staff member on this day
        const dayAttendance = attendance.filter(a => {
          const match = String(a.userId) === String(staffMember.id);
          if (match) {
            try {
              const recordDateStr = format(parseISO(a.date), 'yyyy-MM-dd');
              const dateMatch = recordDateStr === dateStr;
              if (dateMatch) {
                console.log(`Matched! User: ${staffMember.username}, Date: ${dateStr}, Record: ${a.id}`);
              }
              return dateMatch;
            } catch (e) {
              return false;
            }
          }
          return false;
        });

        if (dayAttendance.length === 0) return;

        // Group by pairs of IN/OUT if multiple sessions per day
        const checkIn = dayAttendance.find(a => a.isIn);
        const checkOut = dayAttendance.find(a => !a.isIn);

        let totalMinutes = 0;
        if (checkIn && checkOut) {
          totalMinutes = differenceInMinutes(parseISO(checkOut.date), parseISO(checkIn.date));
        }

        let status: AttendanceEntry['status'] = 'present';
        if (checkIn && !checkOut) {
          status = 'incomplete';
        }

        entries.push({
          id: `${staffMember.id}-${dateStr}`,
          userId: staffMember.id,
          userName: `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim() || staffMember.username || '',
          date: dateStr,
          actualIn: checkIn ? format(parseISO(checkIn.date), 'HH:mm') : undefined,
          actualOut: checkOut ? format(parseISO(checkOut.date), 'HH:mm') : undefined,
          totalHours: Number((totalMinutes / 60).toFixed(2)),
          status,
          originalInId: checkIn?.id,
          originalOutId: checkOut?.id
        });
      });
    });
    return entries;
  };

  const getDateRangeArray = (start: Date, end: Date): string[] => {
    const dates: string[] = [];
    const currentDate = new Date(start);
    const stopDate = new Date(end);
    while (currentDate <= stopDate) {
      dates.push(format(currentDate, 'yyyy-MM-dd'));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const filteredEntries = useMemo(() => {
    let filtered = attendanceEntries;
    if (selectedStaffId !== 'all') {
      filtered = filtered.filter(entry => entry.userId === selectedStaffId);
    }
    return filtered;
  }, [attendanceEntries, selectedStaffId]);

  const summaryStats = useMemo(() => {
    const total = filteredEntries.length;
    const hours = filteredEntries.reduce((acc, curr) => acc + curr.totalHours, 0);

    return { total, hours: hours.toFixed(1) };
  }, [filteredEntries]);

  const handleEditClick = (entry: AttendanceEntry) => {
    setEditingEntry(entry);
    setEditForm({
      date: entry.date,
      clockIn: entry.actualIn || '',
      clockOut: entry.actualOut || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    
    try {
      const updatedInTime = editForm.clockIn ? `${editingEntry.date}T${editForm.clockIn}:00` : null;
      const updatedOutTime = editForm.clockOut ? `${editingEntry.date}T${editForm.clockOut}:00` : null;

      if (editingEntry.originalInId && updatedInTime) {
        await attendanceService.updateAttendanceRecord(editingEntry.originalInId, {
          id: editingEntry.originalInId,
          date: updatedInTime,
          isIn: true,
          userId: editingEntry.userId,
        });
      } else if (!editingEntry.originalInId && updatedInTime) {
        await attendanceService.createAttendanceRecord({
          date: updatedInTime,
          isIn: true,
          userId: editingEntry.userId,
        });
      }

      if (editingEntry.originalOutId && updatedOutTime) {
        await attendanceService.updateAttendanceRecord(editingEntry.originalOutId, {
          id: editingEntry.originalOutId,
          date: updatedOutTime,
          isIn: false,
          userId: editingEntry.userId,
        });
      } else if (!editingEntry.originalOutId && updatedOutTime) {
        await attendanceService.createAttendanceRecord({
          date: updatedOutTime,
          isIn: false,
          userId: editingEntry.userId,
        });
      }

      notify(
        translate(
          'pos.staff.attendance_management.update_success',
          'Attendance record updated successfully!'
        ),
        { type: 'success' }
      );
      setIsEditDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving attendance edit:', error);
      notify(
        translate(
          'pos.staff.attendance_management.update_failed',
          'Failed to update attendance record'
        ),
        { type: 'error' }
      );
    }
  };

  const getStatusBadge = (status: AttendanceEntry['status']) => {
    switch (status) {
      case 'present':
        return <Badge variant="outline" className="bg-green-100 text-green-700">{translate('pos.attendance.status.present', 'Present')}</Badge>;
      case 'incomplete':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-700">{translate('pos.attendance.status.incomplete', 'Incomplete')}</Badge>;
      case 'unscheduled':
        return <Badge variant="outline" className="bg-blue-100 text-blue-700">{translate('pos.attendance.status.unscheduled', 'Unscheduled')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSelectedPreset('custom');
  };

  const handleRangeChange = (from: string, to: string, preset: DateRangePreset) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      setStartDate(from);
      setEndDate(to);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'User Name', 'Date',
      'Actual In', 'Actual Out', 'Total Hours', 'Status'
    ];
    const rows = filteredEntries.map(entry => [
      `"${entry.userName}"`,
      `"${entry.date}"`,
      `"${entry.actualIn || ''}"`,
      `"${entry.actualOut || ''}"`,
      `"${entry.totalHours}"`,
      `"${entry.status}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'attendance_report.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    notify(translate('pos.attendance.export_success', 'Attendance report exported successfully!'), { type: 'success' });
  };

  const renderCalendarContent = () => {
    switch (calendarView) {
      case 'day':
        return renderDayView();
      case 'week':
        return renderWeekView();
      case 'month':
      default:
        return renderMonthView();
    }
  };

  const renderDayView = () => {
    const dayEntries = attendanceEntries.filter(entry => 
      format(parseISO(entry.date), 'yyyy-MM-dd') === format(currentCalendarDate, 'yyyy-MM-dd')
    );

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          {staff.map(s => {
            const entry = dayEntries.find(e => String(e.userId) === String(s.id));
            return (
              <div key={s.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    {s.username?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium">{`${s.firstName || ''} ${s.lastName || ''}`.trim() || s.username}</p>
                    <p className="text-sm text-muted-foreground">{entry ? `${entry.actualIn || '--:--'} - ${entry.actualOut || '--:--'}` : translate('pos.attendance.not_checked_in', 'No attendance')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {entry && (
                    <>
                      <div className="text-right">
                        <p className="font-bold">{entry.totalHours}h</p>
                        {getStatusBadge(entry.status)}
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleEditClick(entry)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {!entry && (
                     <Badge variant="outline" className="opacity-50">{translate('pos.attendance.absent', 'Absent')}</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentCalendarDate, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px] sticky left-0 bg-background">{translate('pos.common.staff', 'Staff')}</TableHead>
              {weekDays.map(day => (
                <TableHead key={day.toString()} className="text-center min-w-[120px]">
                  <p className="font-bold">{format(day, 'EEE')}</p>
                  <p className="text-xs text-muted-foreground">{format(day, 'MMM d')}</p>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {staff.map(s => (
              <TableRow key={s.id}>
                <TableCell className="font-medium sticky left-0 bg-background border-r">
                   {`${s.firstName || ''} ${s.lastName || ''}`.trim() || s.username}
                </TableCell>
                {weekDays.map(day => {
                  const entry = attendanceEntries.find(e => 
                    String(e.userId) === String(s.id) && 
                    format(parseISO(e.date), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')
                  );
                  return (
                    <TableCell key={day.toString()} className="text-center p-1">
                      {entry ? (
                        <div 
                          className="p-2 rounded-md bg-primary/5 hover:bg-primary/10 cursor-pointer transition-colors"
                          onClick={() => handleEditClick(entry)}
                        >
                          <p className="text-xs font-bold">{entry.actualIn} - {entry.actualOut || '??'}</p>
                          <p className="text-[10px] text-muted-foreground">{entry.totalHours}h</p>
                        </div>
                      ) : (
                        <div className="h-10 flex items-center justify-center opacity-20">
                          <Clock className="h-4 w-4" />
                        </div>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderMonthView = () => {
    // Basic Month Grid implementation
    const monthStart = startOfMonth(currentCalendarDate);
    const monthEnd = endOfMonth(currentCalendarDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return (
      <div className="grid grid-cols-7 gap-px bg-muted overflow-hidden rounded-lg border">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(name => (
          <div key={name} className="bg-background p-2 text-center text-xs font-bold text-muted-foreground">
            {name}
          </div>
        ))}
        {days.map((d, i) => {
          const isCurrentMonth = isSameDay(startOfMonth(d), monthStart);
          const isTodayDate = isSameDay(d, new Date());
          const dayEntries = attendanceEntries.filter(e => 
            format(parseISO(e.date), 'yyyy-MM-dd') === format(d, 'yyyy-MM-dd')
          );
          
          return (
            <div 
              key={i} 
              className={`bg-background min-h-[100px] p-2 transition-colors hover:bg-muted/50 cursor-pointer ${!isCurrentMonth ? 'opacity-30' : ''}`}
              onClick={() => {
                setCurrentCalendarDate(d);
                setCalendarView('day');
              }}
            >
              <div className="flex justify-between items-start mb-1">
                <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${isTodayDate ? 'bg-primary text-primary-foreground' : ''}`}>
                  {format(d, 'd')}
                </span>
                {dayEntries.length > 0 && (
                   <Badge variant="secondary" className="text-[10px] h-4 px-1">
                     {dayEntries.length} {translate('pos.common.staff', 'Staff')}
                   </Badge>
                )}
              </div>
              <div className="space-y-1">
                {dayEntries.slice(0, 3).map(e => (
                   <div key={e.id} className="text-[10px] truncate leading-tight bg-primary/5 rounded px-1 py-0.5">
                     <span className="font-bold">{e.actualIn}</span> {e.userName}
                   </div>
                ))}
                {dayEntries.length > 3 && (
                  <p className="text-[9px] text-muted-foreground text-center">+{dayEntries.length - 3} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {translate(
              'pos.attendance.loading',
              'Loading attendance data...'
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.attendance.title', 'Attendance Report')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.attendance.description',
              'Monitor and manage staff attendance records'
            )}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Button onClick={exportToCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            {translate('pos.common.export', 'Export')}
          </Button>
        </div>
      </div>

      {/* Filters and Summary */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>{translate('pos.attendance.filters_summary', 'Filters & Summary')}</CardTitle>
            <CardDescription>{translate('pos.attendance.filters_summary_desc', 'Adjust date range and staff to view attendance data')}</CardDescription>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-muted p-1 rounded-lg">
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className="h-8"
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                {translate('pos.common.calendar', 'Calendar')}
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8"
              >
                <List className="h-4 w-4 mr-2" />
                {translate('pos.common.list', 'List')}
              </Button>
            </div>

            {viewMode === 'calendar' && (
              <div className="flex items-center space-x-2 bg-muted p-1 rounded-lg">
                <Button
                  variant={calendarView === 'day' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setCalendarView('day')}
                  className="h-8"
                >
                  {translate('pos.common.day', 'Day')}
                </Button>
                <Button
                  variant={calendarView === 'week' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setCalendarView('week')}
                  className="h-8"
                >
                  {translate('pos.common.week', 'Week')}
                </Button>
                <Button
                  variant={calendarView === 'month' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setCalendarView('month')}
                  className="h-8"
                >
                  {translate('pos.common.month', 'Month')}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <QuickDateRange
            selectedPreset={selectedPreset}
            onRangeChange={handleRangeChange}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="startDate">{translate('pos.common.start_date', 'Start Date')}</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => handleDateRangeChange(e.target.value, endDate)}
              />
            </div>
            <div>
              <Label htmlFor="endDate">{translate('pos.common.end_date', 'End Date')}</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => handleDateRangeChange(startDate, e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="staffFilter">{translate('pos.common.staff_member', 'Staff Member')}</Label>
              <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                <SelectTrigger id="staffFilter">
                  <SelectValue placeholder={translate('pos.common.select_staff', 'Select staff member')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{translate('pos.common.all_staff', 'All Staff')}</SelectItem>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {`${s.firstName || ''} ${s.lastName || ''}`.trim() || s.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold">{summaryStats.total}</p>
              <p className="text-muted-foreground">{translate('pos.attendance.total_entries', 'Total Entries')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{summaryStats.hours}</p>
              <p className="text-muted-foreground">{translate('pos.attendance.total_hours', 'Total Hours')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Area */}
      {viewMode === 'list' ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {translate('pos.attendance.records_title', 'Attendance Records')}
            </CardTitle>
            <CardDescription>
              {translate('pos.attendance.records_description', 'Detailed view of staff attendance')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{translate('pos.common.staff_member', 'Staff Member')}</TableHead>
                  <TableHead>{translate('pos.common.date', 'Date')}</TableHead>
                  <TableHead>{translate('pos.attendance.actual', 'Actual Time')}</TableHead>
                  <TableHead>{translate('pos.attendance.total_hours', 'Total Hours')}</TableHead>
                  <TableHead>{translate('pos.common.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{translate('pos.common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                      {translate('pos.attendance.no_records', 'No attendance records found for the selected period.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <div className="font-medium">{entry.userName}</div>
                        <div className="text-sm text-muted-foreground">{entry.userId}</div>
                      </TableCell>
                      <TableCell>{format(parseISO(entry.date), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        {entry.actualIn && entry.actualOut
                          ? `${entry.actualIn} - ${entry.actualOut}`
                          : entry.actualIn
                            ? `${entry.actualIn} - ${translate('pos.attendance.no_clock_out', 'No Clock Out')}`
                            : entry.actualOut
                              ? `${translate('pos.attendance.no_clock_in', 'No Clock In')} - ${entry.actualOut}`
                              : translate('pos.common.n_a', 'N/A')}
                      </TableCell>
                      <TableCell>{entry.totalHours > 0 ? `${entry.totalHours}h` : '-'}</TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditClick(entry)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  {translate('pos.attendance.calendar_title', 'Attendance Calendar')}
                </CardTitle>
                <CardDescription>
                  {calendarView === 'day' && format(currentCalendarDate, 'MMMM d, yyyy')}
                  {calendarView === 'week' && `${format(startOfWeek(currentCalendarDate, { weekStartsOn: 1 }), 'MMM d')} - ${format(endOfWeek(currentCalendarDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}`}
                  {calendarView === 'month' && format(currentCalendarDate, 'MMMM yyyy')}
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentCalendarDate(prev => calendarView === 'day' ? subDays(prev, 1) : calendarView === 'week' ? subWeeks(prev, 1) : subMonths(prev, 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentCalendarDate(new Date())}>
                  {translate('pos.common.today', 'Today')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentCalendarDate(prev => calendarView === 'day' ? addDays(prev, 1) : calendarView === 'week' ? addWeeks(prev, 1) : addMonths(prev, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Calendar UI specifically for managerial oversight */}
            <div className="min-h-[400px]">
              {renderCalendarContent()}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{translate('pos.attendance.edit_title', 'Edit Attendance Record')}</DialogTitle>
            <DialogDescription>
              {editingEntry?.userName} - {editingEntry && format(parseISO(editingEntry.date), 'MMM d, yyyy')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="clock-in" className="text-right">Clock In</Label>
              <Input
                id="clock-in"
                type="time"
                className="col-span-3"
                value={editForm.clockIn}
                onChange={(e) => setEditForm(prev => ({ ...prev, clockIn: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="clock-out" className="text-right">Clock Out</Label>
              <Input
                id="clock-out"
                type="time"
                className="col-span-3"
                value={editForm.clockOut}
                onChange={(e) => setEditForm(prev => ({ ...prev, clockOut: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>{translate('pos.common.cancel', 'Cancel')}</Button>
            <Button onClick={handleSaveEdit}>{translate('pos.common.save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernAttendanceManagement;
